import Vue from "vue";
import { DynamicReferenceType } from "~/enums/dynamicReferenceType";
import { ObjectModelBuiltinPropertyTypeName } from "~/enums/objectModelBuiltinPropertyType";
import { CodeMirrorWidget } from "~/helpers/codeMirrorWidget";
import { IDynamicFieldsElementOptions } from "~/interfaces/dynamicFieldsElementOptionsInterfaces";
import { IDynamicReferencedThing } from "~/interfaces/dynamicFieldsInterfaces";
import { IDynamicFieldsOptions } from "~/interfaces/dynamicFieldsInterfaces/dynamicOptionsInterfaces";
import DynamicFieldsHandler from "~/models/dynamicFields/handlers/dynamicFieldsHandler";
import FormElementDynamicFieldsHandler from "~/models/dynamicFields/handlers/formElementDynamicFieldsHandler";
import FormFormulaBuilderDynamicFieldsHandler from "~/models/dynamicFields/handlers/formFormulaBuilderDynamicFieldsHandler";
import ObjectModelPropertyDynamicFieldsHandler from "~/models/dynamicFields/handlers/objectModelPropertyDynamicFieldsHandler";
import FormElement from "../../formability/form_builder/models/formElements/formElement";
import WorkflowStep from "../../formability/form_builder/models/workflow/workflowStep";
import Formula from "../models/formula";
import ObjectModelProperty from "../models/objectModelProperties/objectModelProperty";
import DynamicFieldLookup from "./dynamicFieldLookup";

interface IData {
  editedFormula: string
}

export default Vue.extend({
  data: function () {
    return {
      editedFormula: null,
    };
  },
  mixins: [DynamicFieldLookup],
  props: {
    parent: Object,
    dynamicFieldsHandler: {
      type: Object as () => IDynamicFieldsElementOptions,
    },
    dynamicOptions: {
      type: Array as () => Array<IDynamicFieldsOptions>,
      default: null,
    },
  },
  methods: {
    transformFormulaToHtml(text) {
      if (text) {
        return text.replace(/\bf_(\w+)\b/g, value => {
          const opts = this.getReferencedThingFromFormulaValue(value);
          const el = CodeMirrorWidget.createVarChip(opts);
          return el.outerHTML;
        });
      }
      return '';
    },
    openInsertFormFieldMenuOnClick(e): void {
      if ($(e.target).hasClass('active')) {
        if (this.hasDynamicOptions) this.openPopup(e, 'fields');
      }
    },
    generateEditedFormula() {
      this.editedFormula = new Formula(this.getParent, this.fieldName, this.getValueFromReferenceName);
    },
    getReferencedThingFromFormulaValue(formulaValue, fieldsHandler?: DynamicFieldsHandler): IDynamicReferencedThing {
      // Give control to the dynamic options first
      if (this.dynamicOptions) {
        const referenceFinders = []
        for (const o of this.dynamicOptions as Array<IDynamicFieldsOptions>) {
          if ('referenceFinder' in o) {
            referenceFinders.push(o.referenceFinder)
          }
        }

        if (referenceFinders.length) {
          for (const finder of referenceFinders) {
            const ref = finder(Formula.parseDynamicId(formulaValue))
            if (ref) return ref
          }
        }
      }

      const {id: flatClientId, dynamicReferenceType, meta} = Formula.parseDynamicId(formulaValue)
      if (dynamicReferenceType) {
        let label = "unknown";

        if (dynamicReferenceType === DynamicReferenceType.SubmissionId) {
          label = FormFormulaBuilderDynamicFieldsHandler.SUBMISSION_ID_LABEL;
        } 
        else if (dynamicReferenceType === DynamicReferenceType.ObjectModelRecordMetadata) {
          label = ObjectModelBuiltinPropertyTypeName[Number(flatClientId)];
        } 
        else if (
          dynamicReferenceType === DynamicReferenceType.StepStart ||
          dynamicReferenceType === DynamicReferenceType.StepEnd
        ) {
          const workflowStep = WorkflowStep.repo.findById(flatClientId);
          if (workflowStep) {
            if (dynamicReferenceType === DynamicReferenceType.StepStart) {
              label = `${workflowStep.titleOrDefault} Step Start Date`;
            } else {
              label = `${workflowStep.titleOrDefault} Step Completion Date`;
            }
          } else {
            return null;
          }
        }
        else if (dynamicReferenceType === DynamicReferenceType.PaymentsTotalCost) {
          label = 'Payment Total Amount'
        }
        else if (dynamicReferenceType === DynamicReferenceType.PaymentsType) {
          label = 'Payment Type'
        }
        else if (dynamicReferenceType === DynamicReferenceType.PaymentsDate) {
          label = 'Payment Date'
        }
        else if (dynamicReferenceType === DynamicReferenceType.PaymentsLineItem) {
          label = 'Payment Line Item'
        }
        else if (dynamicReferenceType === DynamicReferenceType.PaymentsReceiptNumber) {
          label = 'Payment Receipt Number'
        }

        return {
          type: dynamicReferenceType,
          id: flatClientId + '_drt' + dynamicReferenceType,
          label: label,
          pillColor: 'light-grey',
        };
      }

      const clientId = Formula.formulaIdToClientId(formulaValue);
      const element = FormElement.repo.find(clientId);
      let pillColor = null;
      if (element) {
        if (fieldsHandler) {
          fieldsHandler.getAll('formFieldData').forEach( (h: FormElementDynamicFieldsHandler) => {
            if (h.form) {
              const elements = h.form.getFormElements;
              if (elements.some( el => el.client_id === clientId)) {
                pillColor = h.pillColor;
              }
            }
          })
        }
        return {
          type: DynamicReferenceType.FormElement,
          id: Formula.buildDynamicId({id: element.client_id, meta: meta}),
          label: element.referenceNameOrDefault,
          wsTitle: element.workflowStep.titleOrDefault,
          pillColor: pillColor,
        };
      }
      const objectModelProperty = ObjectModelProperty.repo.find(clientId);
      if (objectModelProperty) {
        if (fieldsHandler) {
          // TODO: Alex: When when two pill sources are the same object model, then this code is run for both and pill
          //   will always turn the color of that corresponds to the last handler.
          fieldsHandler.getAll('objectModelPropertyData').forEach( (h: ObjectModelPropertyDynamicFieldsHandler) => {
            if (h.objectModel) {
              const elements = h.elementOptions.getElements(h.objectModel);
              if (elements.some( el => el.client_id === clientId)) {
                pillColor = h.pillColor;
              }
            }
          })
        }
        return {
          type: DynamicReferenceType.ObjectModelProperty,
          id: Formula.buildDynamicId({id: objectModelProperty.client_id, meta: meta}),
          label: objectModelProperty.labelTitleDisplay,
          pillColor: pillColor,
        };
      }
      return null;
    },
  },
  computed: {
    formulaText() {
      const formulaValue = this.getParent[`${this.fieldName}_formula`];
      let html = this.transformFormulaToHtml(formulaValue);
      return $(`<div>${html}<div>`).text();
    },
    getDynamicOptions() {
      if (this.dynamicOptions) {
        return this.dynamicOptions;
      } else {
        return [
          {
            is: 'dynamic-fields-tree',
            props: {
              parent: this.parent,
              restrictResultsToParentContext: true,
              fieldsHandler: this.dynamicFieldsHandler,
            },
          },
        ];
      }
    },
    getParent() {
      if (this.parent) {
        return this.parent;
      } else {
        if (this.dynamicOptions && this.dynamicOptions[0] && this.dynamicOptions[0].props) {
          return this.dynamicOptions[0].props.parent;
        }
      }
    },
    getFieldsHandler() {
      if (this.dynamicFieldsHandler) {
        return this.dynamicFieldsHandler;
      } else {
        return this.dynamicOptions[0].props.fieldsHandler;
      }
    },
  },
})
